import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Loader } from "@/components/Loader";
import { usePermissions } from "@/lib/permissions";
import type { Permission } from "@repo/schemas/permissionSchema";

interface ProtectedRouteProps {
  children: React.ReactNode;
  permissions?: Permission[];
  requireAll?: boolean; // true = AND logic, false = OR logic
  fallbackRoute?: string;
}

export function ProtectedRoute({
  children,
  permissions,
  requireAll = true,
  fallbackRoute = "/admin",
}: ProtectedRouteProps) {
  const { user, session, loading, initialized } = useAuthStore();
  const { hasAllPermissions, hasAnyPermission } = usePermissions();
  const location = useLocation();

  // Show loading while auth state is being initialized
  if (!initialized || loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <Loader fullScreen size="lg" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user || !session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check authorization if permissions are required
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      // Log unauthorized access attempt for security monitoring
      console.warn(
        `Unauthorized access attempt to ${location.pathname} by user ${
          user.email
        }. Required permissions: ${permissions.join(", ")}`
      );

      // Redirect to fallback route (could be an unauthorized page or dashboard)
      return <Navigate to={fallbackRoute} replace />;
    }
  }

  // User is authenticated and authorized, render the protected content
  return <>{children}</>;
}

export default ProtectedRoute;
